import { useEffect, useRef, useCallback, type ReactNode } from 'react';

export interface SlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  width?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  footer?: ReactNode;
}

const WIDTH_CLASS: Record<string, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-xl',
};

export default function SlideOver({
  isOpen,
  onClose,
  title,
  subtitle,
  width = 'md',
  children,
  footer,
}: SlideOverProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape key closes
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Focus trap — also redirect focus back if it escapes
  const handleTabKey = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = panelRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) { e.preventDefault(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    // If focus is outside the panel, redirect to first element
    if (!panelRef.current.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
      return;
    }

    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, []);

  // AUDITUI.A3 (#2588) AC5 — return focus to whatever opened the panel.
  //
  // Lives here rather than in a caller's drawer on purpose: all nine existing
  // SlideOver callers have this gap today, and AUDITUI.A4 mounts the pattern
  // five more times. One implementation cannot drift; five copies will.
  //
  // Captured BEFORE the panel takes focus below, because panelRef.focus() on the
  // same tick overwrites document.activeElement — read it first or you capture
  // the panel and restore focus to a hidden element.
  //
  // The restore is in the CLEANUP rather than an `else` branch so it fires on
  // unmount too: 7 of the 9 callers hardcode isOpen and unmount the panel
  // instead of toggling, so an else-branch restore would never run for them.
  //
  // isConnected guards the opener being removed from the DOM while the panel is
  // open — focusing a detached node silently sends focus to <body>, which is
  // worse than leaving it where the user put it. Pinned by R1-6.
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    openerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    document.addEventListener('keydown', handleTabKey);
    // Focus the panel on open
    panelRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener && opener.isConnected) opener.focus();
    };
  }, [isOpen, handleTabKey]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 transition-opacity duration-200"
        style={{
          background: 'rgba(0,0,0,0.4)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-hidden={!isOpen}
        tabIndex={-1}
        className={`fixed top-0 right-0 h-full z-50 flex flex-col w-full ${WIDTH_CLASS[width]} outline-none`}
        style={{
          background: 'var(--surface, #1a1a2e)',
          borderLeft: '1px solid var(--border, rgba(255,255,255,0.1))',
          boxShadow: isOpen ? '-8px 0 30px rgba(0,0,0,0.3)' : 'none',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: isOpen
            ? 'transform 300ms ease-out'
            : 'transform 200ms ease-in',
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-6 py-5 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--border, rgba(255,255,255,0.1))' }}
        >
          <div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 18,
                fontWeight: 700,
                color: 'var(--text)',
                lineHeight: 1.3,
              }}
            >
              {title}
            </h2>
            {subtitle && (
              <p
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                  marginTop: 2,
                }}
              >
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md transition-colors flex-shrink-0 ml-4"
            style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            aria-label="Close panel"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6" style={{ minHeight: 0 }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className="flex-shrink-0 px-6 py-4"
            style={{ borderTop: '1px solid var(--border, rgba(255,255,255,0.1))' }}
          >
            {footer}
          </div>
        )}
      </div>
    </>
  );
}
